import React from 'react';
import {
  Typography,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Alert,
  Chip,
  Divider,
  Grid,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import SkipIcon from '@mui/icons-material/SkipNext';
import UpdateIcon from '@mui/icons-material/Update';
import { BatchItemResultDto } from '../../api/types/dtos/organization.dto';

interface UploadReportProps {
  reportData?: BatchItemResultDto[] | null;
  updatedData?: BatchItemResultDto[] | null;
  skippedData?: BatchItemResultDto[] | null;
  failedData?: BatchItemResultDto[] | null;
  error?: string | null;
  mode?: 'upsert' | 'delete';
}

const UploadReport: React.FC<UploadReportProps> = ({
  reportData = null,
  updatedData = null,
  skippedData = null,
  failedData = null,
  error = null,
  mode = 'upsert',
}) => {
  const created = reportData || [];
  const updated = updatedData || [];
  const skipped = skippedData || [];
  const failed = failedData || [];
  const total = created.length + updated.length + skipped.length + failed.length;

  if (total === 0) {
    return <Alert severity="info">No hay datos de reporte disponibles.</Alert>;
  }

  const renderRow = (item: BatchItemResultDto, index: number, statusLabel: string, icon: React.ReactNode) => (
    <React.Fragment key={`${item.idNumber || 'item'}-${index}`}>
      <ListItem>
        <ListItemIcon sx={{ minWidth: '40px' }}>{icon}</ListItemIcon>
        <ListItemText
          primary={`${item.name || 'N/D'} (ID: ${item.idNumber || 'N/D'})`}
          secondary={`Estado: ${statusLabel}${item.errorMessage ? ` - ${item.errorMessage}` : ''}`}
        />
      </ListItem>
      {index < total - 1 && <Divider component="li" />}
    </React.Fragment>
  );

  const allItems = [
    ...created.map((item, i) => ({ item, i, status: mode === 'delete' ? 'Eliminada' : 'Creada', icon: <CheckCircleIcon color="success" /> })),
    ...updated.map((item, i) => ({ item, i, status: 'Actualizada', icon: <UpdateIcon color="info" /> })),
    ...skipped.map((item, i) => ({ item, i, status: 'Omitida', icon: <SkipIcon color="warning" /> })),
    ...failed.map((item, i) => ({ item, i, status: 'Fallida', icon: <ErrorIcon color="error" /> })),
  ];

  return (
    <Paper elevation={0} sx={{ p: 2 }}>
      <Typography variant="h5" gutterBottom align="center">
        Reporte de {mode === 'delete' ? 'Eliminacion' : 'Operacion'} por Lotes
      </Typography>

      <Grid container spacing={2} sx={{ mb: 3, textAlign: 'center' }}>
        {created.length > 0 && (
          <Grid item xs={6} sm={3}>
            <Typography variant="subtitle1">{mode === 'delete' ? 'Eliminadas' : 'Creadas'}</Typography>
            <Chip
              icon={<CheckCircleIcon />}
              label={created.length}
              color="success"
              sx={{ fontSize: '1.2rem', p: 2 }}
            />
          </Grid>
        )}
        {updated.length > 0 && (
          <Grid item xs={6} sm={3}>
            <Typography variant="subtitle1">Actualizadas</Typography>
            <Chip
              icon={<UpdateIcon />}
              label={updated.length}
              color="info"
              sx={{ fontSize: '1.2rem', p: 2 }}
            />
          </Grid>
        )}
        {skipped.length > 0 && (
          <Grid item xs={6} sm={3}>
            <Typography variant="subtitle1">Omitidas</Typography>
            <Chip
              icon={<SkipIcon />}
              label={skipped.length}
              color="warning"
              sx={{ fontSize: '1.2rem', p: 2 }}
            />
          </Grid>
        )}
        {failed.length > 0 && (
          <Grid item xs={6} sm={3}>
            <Typography variant="subtitle1">Fallidas</Typography>
            <Chip
              icon={<ErrorIcon />}
              label={failed.length}
              color="error"
              sx={{ fontSize: '1.2rem', p: 2 }}
            />
          </Grid>
        )}
      </Grid>

      {error && <Alert severity="warning" sx={{ mb: 2 }}>{error}</Alert>}
      {failed.length > 0 && !error && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Algunos registros no pudieron ser procesados. Revisa los detalles a continuacion.
        </Alert>
      )}
      {failed.length === 0 && skipped.length === 0 && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Todos los registros fueron procesados exitosamente.
        </Alert>
      )}

      <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>Detalles:</Typography>
      <List dense sx={{ maxHeight: 400, overflow: 'auto', border: '1px solid #ddd', borderRadius: '4px' }}>
        {allItems.map((entry, idx) => renderRow(entry.item, idx, entry.status, entry.icon))}
      </List>
    </Paper>
  );
};

export default UploadReport;
